import {Form} from '../../../../stores/Form/Form';
import {isRTL} from '../../../../Localization';
import {keys} from '../../../../constants/KeyCodes';
import {ColumnsTabsDesignType, ColumnTabsDesignType} from '../../../../stores/Form/utils';

const rtl = isRTL();

export type NavDataType = {
    ColumnId: number;
    ActiveTab: number;
} | null;

const navKeyMap = {
    [keys.ARROW_RIGHT]: true,
    [keys.ARROW_LEFT]: true,
    [keys.ARROW_DOWN]: true,
    [keys.ARROW_UP]: true,
    [keys.HOME]: true,
    [keys.END]: true,
    [keys.ENTER]: true,
    [keys.TAB]: true
};

const getNextTabFirstColumn = (columnsTabs: ColumnsTabsDesignType, openTabId: number) => {
    return (
        columnsTabs.find(column => column.TabId === openTabId + 1) ||
        columnsTabs.find(column => column.TabId === 1)
    );
};

const getPrevTabFirstColumn = (columnsTabs: ColumnsTabsDesignType, openTabId: number) => {
    const prevTabId = openTabId - 1;
    const lastTabId = columnsTabs[columnsTabs.length - 1].TabId;
    return prevTabId > 0
        ? columnsTabs.find(column => column.TabId === prevTabId)
        : columnsTabs.find(column => column.TabId === lastTabId);
};

const getNextRowSibling = (
    columnsTabs: ColumnsTabsDesignType,
    currentColumn: ColumnTabsDesignType,
    openTabId: number,
    fixedTabId: number
) => {
    const nextColumn = columnsTabs.find(column => {
        return (
            column.Index > currentColumn.Index &&
            column.TabSectionRow === currentColumn.TabSectionRow &&
            (column.TabId === openTabId || column.TabId === fixedTabId)
        );
    });

    return nextColumn || currentColumn;
};

const getPrevRowSibling = (
    columnsTabs: ColumnsTabsDesignType,
    currentColumn: ColumnTabsDesignType,
    openTabId: number,
    fixedTabId: number
) => {
    const areaColumns = columnsTabs.filter(
        column =>
            column.Index < currentColumn.Index &&
            column.TabSectionRow === currentColumn.TabSectionRow &&
            (column.TabId === openTabId || column.TabId === fixedTabId)
    );

    return areaColumns[areaColumns.length - 1] || currentColumn;
};

const getNavigationData = (e: KeyboardEvent, form: Form, openTabId: number): NavDataType => {
    const {currentColumnId, columnsTabs} = form;

    const currentColumn = columnsTabs.find(c => c.ColumnId === currentColumnId);
    const fixedTabId = columnsTabs[0]?.TabId;

    if (e.key === keys.ARROW_LEFT) {
        if (e.shiftKey) {
            const handler = rtl ? getNextTabFirstColumn : getPrevTabFirstColumn;
            const nextColumn = handler(columnsTabs, openTabId);
            return {ColumnId: nextColumn.ColumnId, ActiveTab: nextColumn.TabId};
        }

        const navigationHandler = rtl ? getNextRowSibling : getPrevRowSibling;
        const validatedColumn = navigationHandler(
            columnsTabs,
            currentColumn,
            openTabId,
            fixedTabId
        );

        return {
            ColumnId: validatedColumn.ColumnId,
            ActiveTab: validatedColumn.TabId
        };
    } else if (e.key === keys.ARROW_RIGHT) {
        if (e.shiftKey) {
            const handler = rtl ? getPrevTabFirstColumn : getNextTabFirstColumn;
            const nextColumn = handler(columnsTabs, openTabId);
            return {ColumnId: nextColumn.ColumnId, ActiveTab: nextColumn.TabId};
        }

        const navigationHandler = rtl ? getPrevRowSibling : getNextRowSibling;
        const validatedColumn = navigationHandler(
            columnsTabs,
            currentColumn,
            openTabId,
            fixedTabId
        );

        return {
            ColumnId: validatedColumn.ColumnId,
            ActiveTab: validatedColumn.TabId
        };
    } else if (e.key === keys.ARROW_UP) {
        const nextColumn = columnsTabs[currentColumn.Index - 1];
        const validatedColumn =
            nextColumn &&
            nextColumn.TabId === currentColumn.TabId &&
            nextColumn.TabSection === currentColumn.TabSection
                ? nextColumn
                : currentColumn;

        return {
            ColumnId: validatedColumn.ColumnId,
            ActiveTab: validatedColumn.TabId
        };
    } else if (e.key === keys.ARROW_DOWN) {
        const nextColumn = columnsTabs[currentColumn.Index + 1];
        const validatedColumn =
            nextColumn &&
            nextColumn.TabId === currentColumn.TabId &&
            nextColumn.TabSection === currentColumn.TabSection
                ? nextColumn
                : currentColumn;

        return {
            ColumnId: validatedColumn.ColumnId,
            ActiveTab: validatedColumn.TabId
        };
    } else if (e.key === keys.HOME) {
        return {
            ColumnId: columnsTabs[0].ColumnId,
            ActiveTab: columnsTabs[0].TabId
        };
    } else if (e.key === keys.END) {
        const areaColumns = columnsTabs.filter(col => col.TabId === openTabId);
        const nextColumn = areaColumns[areaColumns.length - 1];

        return {
            ColumnId: nextColumn?.ColumnId,
            ActiveTab: nextColumn?.TabId
        };
    } else if (e.key === keys.TAB || e.key === keys.ENTER) {
        let nextColumn;

        if (e.key === keys.ENTER && Boolean(form.searchModeLevel)) {
            return null;
        }

        if (e.shiftKey && e.key === keys.TAB) {
            const areaColumns = columnsTabs
                .filter(
                    column =>
                        column.Index < currentColumn.Index &&
                        (column.TabId === openTabId || column.TabId === fixedTabId)
                )
                .reverse();

            nextColumn = areaColumns[0] || currentColumn;
        } else {
            let hasFallback = false;

            for (const column of columnsTabs) {
                if (
                    column.GeneralRowIndex > currentColumn.GeneralRowIndex &&
                    (column.TabId === fixedTabId || column.TabId >= openTabId)
                ) {
                    nextColumn = column;
                    break;
                }

                if (!hasFallback && column.TabId === fixedTabId + 1) {
                    // fallback column for circular navigation
                    nextColumn = column;
                    hasFallback = true;
                }
            }
        }
        return {
            ColumnId: nextColumn.ColumnId,
            ActiveTab: nextColumn.TabId
        };
    }
    return null;
};

export const formViewNav = (
    e: KeyboardEvent,
    form: Form,
    openTabId: number,
    callback?: (params: NavDataType) => void
) => {
    if (navKeyMap[e.key] && !!form.columnsTabs.length && !form.isApiLock()) {
        e.preventDefault();
        const navData = getNavigationData(e, form, openTabId);

        if (callback && navData) {
            callback(navData);
        }
    }
};
